// Keep Essence's modal, focus trap, submit and 250ms debounce; override search transport only.
await customElements.whenDefined('search-modal');
const SearchModal = customElements.get('search-modal');

class HFSearchModal extends SearchModal {
  connectedCallback() {
    const content = this.querySelector('[slot="content"]');
    if (!content?.querySelector('input[type="search"]') ||
        !content.querySelector('[data-results-container]') || !content.querySelector('[data-skeleton]')) return;
    if (!this.modalInitialized) {
      super.connectedCallback();
      this.modalInitialized = true;
    } else {
      this.setupElements();
      this.setupListeners();
      if (window.Shopify.designMode && !this.designModeInit) this.initDesignMode();
    }
    this.editorSection = this.closest('.shopify-section');
    this.searchStatus = content.querySelector('[data-search-status]');
    try {
      this.predictiveUnavailable = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === false;
    } catch { this.predictiveUnavailable = false; }
  }

  setupListeners() {
    this.inputListener ||= this.onSearchInput.bind(this);
    this.boundInput?.removeEventListener('input', this.inputListener);
    this.boundInput = this.searchInput;
    this.boundInput.addEventListener('input', this.inputListener);
  }

  disconnectedCallback() {
    this.cancelSearch();
    this.boundInput?.removeEventListener('input', this.inputListener);
    if (this.open) this.hide({ anim: false });
    // Essence's teardown expects closest(section), which is absent after Editor removal.
    if (this.designModeInit && this.editorSection) {
      this.editorSection.removeEventListener('shopify:section:load', this.onShopifySectionLoaded);
      this.editorSection.removeEventListener('shopify:section:select', this.onShopifySectionSelected);
      this.editorSection.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselected);
      this.designModeInit = false;
    }
  }

  cancelSearch() {
    this.requestVersion = (this.requestVersion || 0) + 1;
    this.abortController?.abort();
    this.resultsContainer?.setAttribute('aria-busy', 'false');
  }

  onSearchInput() {
    this.cancelSearch(); // Invalidate immediately, including the debounce interval.
    super.onSearchInput();
  }

  getFallbackURL(query) {
    const url = new URL(this.searchInput.form.action, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', 'hf-search-suggestions');
    return url;
  }

  parseResults(html) {
    if (typeof html !== 'string' || !html.trim()) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('#shopify-section-predictive-search, #shopify-section-hf-search-suggestions');
    // Neither API error text/JSON nor arbitrary error pages are candidate markup.
    return root?.children.length && !root.querySelector('script') ? root : null;
  }

  async fetchResults(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) return null;
    const type = response.headers.get('content-type') || '';
    if (!type.includes('text/html')) return null;
    const html = await response.text();
    return this.parseResults(html) ? html : null;
  }

  async performSearch() {
    const query = this.getSearchQuery();
    if (!this.isConnected || !this.open || !query.trim()) return;
    this.abortController?.abort();
    const controller = this.abortController = new AbortController();
    const version = this.requestVersion;
    const current = () => !controller.signal.aborted && this.isConnected && this.open &&
      version === this.requestVersion && query === this.getSearchQuery();
    try {
      let html = null;
      if (!this.predictiveUnavailable) {
        try { html = await this.fetchResults(this.getSearchURL(query), controller.signal); }
        catch (error) { if (error.name === 'AbortError') return; }
      }
      if (!current()) return;
      if (!html) html = await this.fetchResults(this.getFallbackURL(query), controller.signal);
      if (!current()) return;
      if (html) {
        if (this.searchCache.size >= 50) this.searchCache.delete(this.searchCache.keys().next().value);
        this.searchCache.set(query, html); // Cache only validated successes.
        this.showResults(html);
      } else this.showUnavailable(query);
    } catch (error) {
      if (error.name !== 'AbortError' && current()) this.showUnavailable(query);
    } finally {
      if (current()) this.resultsContainer.setAttribute('aria-busy', 'false');
    }
  }

  showSkeleton() {
    super.showSkeleton();
    this.resultsContainer.setAttribute('aria-busy', 'true');
  }

  showResults(html) {
    const root = this.parseResults(html);
    if (!root || !this.resultsContainer) return false;
    this.hideSkeleton();
    this.hidePlaceholder();
    this.resultsContainer.replaceChildren(...root.children);
    this.resultsContainer.classList.remove('hidden');
    this.resultsContainer.setAttribute('aria-busy', 'false');
    this.searchInput.setAttribute('aria-expanded', 'true');
    if (this.searchStatus) this.searchStatus.textContent =
      this.resultsContainer.querySelector('[data-search-result-status]')?.dataset.searchResultStatus ||
      this.resultsContainer.textContent.trim();
    return true;
  }

  showUnavailable(query) {
    this.clearResults();
    this.hideSkeleton();
    const template = this.querySelector('[data-search-unavailable]');
    if (!template) return;
    const content = template.content.cloneNode(true);
    const link = content.querySelector('a');
    const url = this.getFallbackURL(query);
    url.searchParams.delete('section_id');
    link.href = url;
    this.hidePlaceholder();
    this.resultsContainer.replaceChildren(content);
    this.resultsContainer.classList.remove('hidden');
    this.searchInput.setAttribute('aria-expanded', 'true');
    if (this.searchStatus) this.searchStatus.textContent = link.textContent;
  }

  clearResults() {
    super.clearResults();
    this.resultsContainer.setAttribute('aria-busy', 'false');
    this.searchInput.setAttribute('aria-expanded', 'false');
    if (this.searchStatus) this.searchStatus.textContent = '';
  }

  show(...args) {
    if (this.searchOpening) return this.searchOpening;
    this.searchOpening = (async () => {
      await this.searchClosing;
      if (this.isConnected) await super.show(...args);
    })().finally(() => { this.searchOpening = null; });
    return this.searchOpening;
  }

  hide(...args) {
    if (this.searchClosing) return this.searchClosing;
    this.cancelSearch();
    // Essence activates its focus trap after the opening animation. Finish that
    // animation before hiding, including when cached results allow an immediate ESC.
    this.searchClosing = (async () => {
      await this.searchOpening;
      await super.hide(...args);
    })().finally(() => { this.searchClosing = null; });
    return this.searchClosing;
  }

  async beforeHide() {
    this.cancelSearch();
    this.clearInput(); // A queued Essence debounce must not restart a closed search.
    this.searchInput.setAttribute('aria-expanded', 'false');
    await super.beforeHide();
  }

  async afterHide() {
    await super.afterHide();
    if (this.isConnected && !this.open && this.activatorElement?.isConnected) {
      this.activatorElement.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('hf-search-modal')) customElements.define('hf-search-modal', HFSearchModal);
